/*@flow*/
import isNil from 'lib/t/isNil';
import isString from 'lib/t/isString';
import isNumber from 'lib/t/isNumber';
import isDate from 'lib/t/isDate';
import isArray from 'lib/t/isArray';
import type { TransformerT } from '../types';

const identity = (_: any): any => _;

export const idTrans = {
  format: identity,
  parse: identity,
};

function toNull(value: any) {
  return (isString(value) && value.trim() === '') || isNil(value)
    ? null
    : value;
}

function parseNumber(value: any) {
  const n = parseFloat(value);
  const isNumeric = value == n; // eslint-disable-line eqeqeq
  return isNumeric ? n : toNull(value);
}

const nodatetime = Object.freeze([null, null, null]);
const noobj = Object.freeze({});
const noarr = Object.freeze([]);

const nilTx = (nilValue: any) => (v: any) => (isNil(v) ? nilValue : v);

function formatDate(v: any): [any, any, any] {
  if (isArray(v)) {
    return v;
  } else if (isDate(v)) {
    return [String(v.getFullYear()), String(v.getMonth()), String(v.getDate())];
  } else if (isString(v)) {
    return formatDate(new Date(v));
  }
  return nodatetime;
}

function parseDate(v: any) {
  if (isArray(v)) {
    const numbers: any = v.map(parseNumber);
    if (numbers.every(isNumber)) {
      return new Date(numbers[0], numbers[1], numbers[2]);
    } else if (numbers.every(isNil)) {
      return null;
    }
    return numbers;
  } else if (isString(v)) {
    try {
      const date = new Date(v);
      if (date.toString() === 'Invalid Date') {
        return null;
      }
      return date;
    } catch (_) {
      return null;
    }
  } else if (isDate(v)) {
    return v;
  }
}

const transformers = {
  struct: {
    format: nilTx(noobj),
    parse: identity,
  },
  text: {
    format: nilTx(''),
    parse: toNull,
  },
  number: {
    format: (v: any) => (isNil(v) ? '' : String(v)),
    parse: parseNumber,
  },
  singleSelect: (nullOption: { value: any, text: string }) => ({
    format: (v: any) => (isNil(v) && nullOption ? nullOption.value : v),
    parse: (v: any) => (nullOption && nullOption.value === v ? null : v),
  }),
  multiSelect: {
    format: nilTx(noarr),
    parse: identity,
  },
  checkbox: {
    format: nilTx(false),
    parse: identity,
  },
  radio: {
    format: nilTx(null),
    parse: identity,
  },
  list: {
    format: nilTx(noarr),
    parse: identity,
  },
  datetime: {
    format: formatDate,
    parse: parseDate,
  },
};

function getOptionTxIfExists(options) {
  const tx = options.transformer || options.tx;
  if (!tx) return;
  if (typeof tx === 'function') return { format: tx, parse: tx };
  return tx;
}

export default function getTransformer(
  options: $Shape<{
    transformer?: TransformerT,
    tx?: TransformerT | (any => any),
  }>,
  transName: string
):
  | any
  | TransformerT
  | {|
    format: TransformerT | ((any) => any),
    parse: TransformerT | ((any) => any),
  |} {
  const optionTx = getOptionTxIfExists(options);

  return optionTx || transformers[transName] || idTrans;
}
